/**
 * Photo Annotation Service
 * Canvas-based drawing and markup tools
 */

export interface DrawingTool {
  type: 'pen' | 'highlighter' | 'arrow' | 'rectangle' | 'circle' | 'text';
  color: string;
  size: number;
}

export interface AnnotationPoint {
  x: number;
  y: number;
}

export interface Annotation {
  id: string;
  tool: DrawingTool;
  points: AnnotationPoint[];
  text?: string;
  timestamp: number;
}

export class PhotoAnnotationCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private isDrawing = false;
  private currentAnnotation: Annotation | null = null;
  private annotations: Annotation[] = [];
  private currentTool: DrawingTool = {
    type: 'pen',
    color: '#FF0000',
    size: 3,
  };
  private startPoint: AnnotationPoint | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get canvas context');
    }
    this.ctx = context;
    
    this.setupEventListeners();
  }

  private setupEventListeners() {
    // Mouse events
    this.canvas.addEventListener('mousedown', (e) => this.handleStart(e.offsetX, e.offsetY));
    this.canvas.addEventListener('mousemove', (e) => this.handleMove(e.offsetX, e.offsetY));
    this.canvas.addEventListener('mouseup', () => this.handleEnd());
    this.canvas.addEventListener('mouseleave', () => this.handleEnd());

    // Touch events
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.handleStart(touch.clientX - rect.left, touch.clientY - rect.top);
    });

    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.handleMove(touch.clientX - rect.left, touch.clientY - rect.top);
    });

    this.canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.handleEnd();
    });
  }

  private handleStart(x: number, y: number) {
    this.isDrawing = true;
    this.startPoint = { x, y };
    
    this.currentAnnotation = {
      id: `annotation-${Date.now()}-${Math.random()}`,
      tool: { ...this.currentTool },
      points: [{ x, y }],
      timestamp: Date.now(),
    };
  }

  private handleMove(x: number, y: number) {
    if (!this.isDrawing || !this.currentAnnotation) return;

    const point = { x, y };
    
    if (this.currentTool.type === 'pen' || this.currentTool.type === 'highlighter') {
      // Add point to path
      this.currentAnnotation.points.push(point);
      this.redraw();
    } else {
      // For shapes, update end point
      if (this.currentAnnotation.points.length === 1) {
        this.currentAnnotation.points.push(point);
      } else {
        this.currentAnnotation.points[1] = point;
      }
      this.redraw();
    }
  }

  private handleEnd() {
    if (!this.isDrawing || !this.currentAnnotation) return;

    this.isDrawing = false;
    
    // Save annotation if it has meaningful content
    if (this.currentAnnotation.points.length > 1) {
      this.annotations.push(this.currentAnnotation);
    }
    
    this.currentAnnotation = null;
    this.startPoint = null;
  }

  private redraw() {
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw all saved annotations
    this.annotations.forEach(annotation => this.drawAnnotation(annotation));

    // Draw current annotation
    if (this.currentAnnotation) {
      this.drawAnnotation(this.currentAnnotation);
    }
  }

  private drawAnnotation(annotation: Annotation) {
    const { tool, points } = annotation;

    this.ctx.strokeStyle = tool.color;
    this.ctx.lineWidth = tool.size;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    if (tool.type === 'pen') {
      this.drawPath(points);
    } else if (tool.type === 'highlighter') {
      this.ctx.globalAlpha = 0.4;
      this.ctx.lineWidth = tool.size * 3;
      this.drawPath(points);
      this.ctx.globalAlpha = 1.0;
    } else if (tool.type === 'arrow') {
      this.drawArrow(points[0], points[points.length - 1], tool.size);
    } else if (tool.type === 'rectangle') {
      this.drawRectangle(points[0], points[points.length - 1]);
    } else if (tool.type === 'circle') {
      this.drawCircle(points[0], points[points.length - 1]);
    }
  }

  private drawPath(points: AnnotationPoint[]) {
    if (points.length < 2) return;

    this.ctx.beginPath();
    this.ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      this.ctx.lineTo(points[i].x, points[i].y);
    }

    this.ctx.stroke();
  }

  private drawArrow(start: AnnotationPoint, end: AnnotationPoint, size: number) {
    // Draw line
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.stroke();

    // Draw arrowhead
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const arrowLength = size * 4;

    this.ctx.beginPath();
    this.ctx.moveTo(end.x, end.y);
    this.ctx.lineTo(
      end.x - arrowLength * Math.cos(angle - Math.PI / 6),
      end.y - arrowLength * Math.sin(angle - Math.PI / 6)
    );
    this.ctx.moveTo(end.x, end.y);
    this.ctx.lineTo(
      end.x - arrowLength * Math.cos(angle + Math.PI / 6),
      end.y - arrowLength * Math.sin(angle + Math.PI / 6)
    );
    this.ctx.stroke();
  }

  private drawRectangle(start: AnnotationPoint, end: AnnotationPoint) {
    const width = end.x - start.x;
    const height = end.y - start.y;
    
    this.ctx.beginPath();
    this.ctx.rect(start.x, start.y, width, height);
    this.ctx.stroke();
  }

  private drawCircle(start: AnnotationPoint, end: AnnotationPoint) {
    const radius = Math.sqrt(
      Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
    );
    
    this.ctx.beginPath();
    this.ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
    this.ctx.stroke();
  }

  setTool(tool: Partial<DrawingTool>) {
    this.currentTool = { ...this.currentTool, ...tool };
  }

  getTool(): DrawingTool {
    return { ...this.currentTool };
  }

  undo() {
    this.annotations.pop();
    this.redraw();
  }

  clear() {
    this.annotations = [];
    this.redraw();
  }

  getAnnotations(): Annotation[] {
    return [...this.annotations];
  }

  setAnnotations(annotations: Annotation[]) {
    this.annotations = annotations;
    this.redraw();
  }

  exportAsDataURL(): string {
    return this.canvas.toDataURL('image/png');
  }

  destroy() {
    // Remove event listeners
    this.canvas.removeEventListener('mousedown', () => {});
    this.canvas.removeEventListener('mousemove', () => {});
    this.canvas.removeEventListener('mouseup', () => {});
    this.canvas.removeEventListener('mouseleave', () => {});
    this.canvas.removeEventListener('touchstart', () => {});
    this.canvas.removeEventListener('touchmove', () => {});
    this.canvas.removeEventListener('touchend', () => {});
  }
}
